import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RevealIconButton } from '../../components/ui/RevealIconButton';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import {
  buildCacheEntryKey,
  CacheEntry,
  CacheScopeSummary,
  clearAllCache,
  clearAllMonitoringAuditEntries,
  clearAllMonitoringLogs,
  fetchCacheEntries,
  fetchCacheOverview,
  fetchMonitoringAudit,
  fetchMonitoringLogs,
  invalidateCacheScope,
  MonitoringAuditEntry,
  MonitoringLogEntry,
  removeCacheEntries,
  removeMonitoringAuditEntries,
  removeMonitoringLogs,
} from '../../services/monitoringApi';
import { MonitoringLogDetailModal } from './MonitoringLogDetailModal';
import { MonitoringTabId } from './MonitoringTabId';

const MONITORING_TABS: SettingsTabItem<MonitoringTabId>[] = [
  { id: 'logs', label: 'Logs' },
  { id: 'audit', label: 'Auditoria' },
  { id: 'cache', label: 'Cache' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const LOG_LEVEL_OPTIONS = ['Exception', 'Error', 'Critical', 'Warning'] as const;

const AUDIT_ACTION_OPTIONS = ['SoftDelete', 'HardDelete', 'StatusChange', 'Create', 'Update'] as const;

function truncateText(value: string | null | undefined, maxLength = 80): string {
  if (!value) {
    return '—';
  }
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}…`;
}

function toDateParam(value: string, endOfDay = false): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

export function MonitoringPage() {
  const { confirm, confirmDelete } = useMessageBox();
  const [activeTab, setActiveTab] = useState<MonitoringTabId>('logs');

  const [cacheScopes, setCacheScopes] = useState<CacheScopeSummary[]>([]);
  const [cacheEntries, setCacheEntries] = useState<CacheEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);
  const [cachePage, setCachePage] = useState(1);
  const [cachePageSize, setCachePageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [scopeFilter, setScopeFilter] = useState('');
  const [selectedCacheKeys, setSelectedCacheKeys] = useState<Set<string>>(new Set());
  const [invalidatingScope, setInvalidatingScope] = useState<string | null>(null);

  const [logs, setLogs] = useState<MonitoringLogEntry[]>([]);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [logLevelFilter, setLogLevelFilter] = useState('');
  const [logFromDate, setLogFromDate] = useState('');
  const [logToDate, setLogToDate] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [appliedLogLevel, setAppliedLogLevel] = useState('');
  const [appliedLogFromDate, setAppliedLogFromDate] = useState('');
  const [appliedLogToDate, setAppliedLogToDate] = useState('');
  const [appliedLogSearch, setAppliedLogSearch] = useState('');
  const [selectedLogIds, setSelectedLogIds] = useState<Set<string>>(new Set());
  const [detailLog, setDetailLog] = useState<MonitoringLogEntry | null>(null);

  const [auditEntries, setAuditEntries] = useState<MonitoringAuditEntry[]>([]);
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditFromDate, setAuditFromDate] = useState('');
  const [auditToDate, setAuditToDate] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [appliedAuditAction, setAppliedAuditAction] = useState('');
  const [appliedAuditFromDate, setAppliedAuditFromDate] = useState('');
  const [appliedAuditToDate, setAppliedAuditToDate] = useState('');
  const [appliedAuditSearch, setAppliedAuditSearch] = useState('');
  const [selectedAuditIds, setSelectedAuditIds] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const scopeOptions = useMemo(
    () => cacheScopes.map((scope) => ({ value: scope.scope, label: scope.label })),
    [cacheScopes],
  );

  const auditActionOptions = useMemo(() => {
    const fromPage = auditEntries.map((entry) => entry.action).filter(Boolean);
    return Array.from(new Set<string>([...AUDIT_ACTION_OPTIONS, ...fromPage])).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [auditEntries]);

  const allCachePageSelected =
    cacheEntries.length > 0
    && cacheEntries.every((entry) => selectedCacheKeys.has(buildCacheEntryKey(entry)));

  const allLogsPageSelected =
    logs.length > 0 && logs.every((entry) => selectedLogIds.has(entry.id));

  const allAuditPageSelected =
    auditEntries.length > 0 && auditEntries.every((entry) => selectedAuditIds.has(entry.id));

  const loadCacheData = useCallback(async (page: number, scope: string, pageSize: number): Promise<void> => {
    const [overview, entries] = await Promise.all([
      fetchCacheOverview(),
      fetchCacheEntries(page, pageSize, scope || undefined),
    ]);
    setCacheScopes(overview.scopes);
    setTotalEntries(overview.totalEntries);
    setCacheEntries(entries.items);
    setEntriesTotalCount(entries.totalCount);
    setCachePage(entries.page);
  }, []);

  const loadLogs = useCallback(async (page: number, pageSize: number): Promise<void> => {
    const result = await fetchMonitoringLogs({
      page,
      pageSize,
      level: appliedLogLevel || undefined,
      search: appliedLogSearch || undefined,
      fromUtc: toDateParam(appliedLogFromDate),
      toUtc: toDateParam(appliedLogToDate, true),
    });
    setLogs(result.items);
    setLogsTotalCount(result.totalCount);
    setLogsPage(result.page);
    setSelectedLogIds(new Set());
  }, [appliedLogLevel, appliedLogSearch, appliedLogFromDate, appliedLogToDate]);

  const loadAudit = useCallback(async (page: number, pageSize: number): Promise<void> => {
    const result = await fetchMonitoringAudit({
      page,
      pageSize,
      action: appliedAuditAction || undefined,
      search: appliedAuditSearch || undefined,
      fromUtc: toDateParam(appliedAuditFromDate),
      toUtc: toDateParam(appliedAuditToDate, true),
    });
    setAuditEntries(result.items);
    setAuditTotalCount(result.totalCount);
    setAuditPage(result.page);
    setSelectedAuditIds(new Set());
  }, [appliedAuditAction, appliedAuditSearch, appliedAuditFromDate, appliedAuditToDate]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        if (activeTab === 'cache') {
          await loadCacheData(cachePage, scopeFilter, cachePageSize);
        } else if (activeTab === 'logs') {
          await loadLogs(logsPage, logsPageSize);
        } else if (activeTab === 'audit') {
          await loadAudit(auditPage, auditPageSize);
        }
      } catch {
        toast.error('Falha ao carregar dados de monitoramento.');
      } finally {
        setLoading(false);
      }
    })();
  }, [
    activeTab,
    cachePage,
    cachePageSize,
    scopeFilter,
    loadCacheData,
    logsPage,
    logsPageSize,
    loadLogs,
    auditPage,
    auditPageSize,
    loadAudit,
  ]);

  async function refreshCacheTab(page = cachePage): Promise<void> {
    setSelectedCacheKeys(new Set());
    await loadCacheData(page, scopeFilter, cachePageSize);
  }

  async function handleInvalidateScope(scope: CacheScopeSummary): Promise<void> {
    if (
      !(await confirm({
        title: 'Invalidar cache',
        message: `Invalidar o cache "${scope.label}"?`,
        confirmLabel: 'Invalidar',
        tone: 'danger',
      }))
    ) {
      return;
    }

    setInvalidatingScope(scope.scope);
    try {
      await invalidateCacheScope(scope.scope, scope.customerId);
      toast.success(`Cache "${scope.label}" invalidado.`);
      setCachePage(1);
      await refreshCacheTab(1);
    } catch {
      toast.error('Falha ao invalidar cache.');
    } finally {
      setInvalidatingScope(null);
    }
  }

  function toggleCacheEntrySelection(entry: CacheEntry): void {
    const key = buildCacheEntryKey(entry);
    setSelectedCacheKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function toggleSelectAllCacheOnPage(): void {
    setSelectedCacheKeys((current) => {
      const next = new Set(current);
      if (allCachePageSelected) {
        cacheEntries.forEach((entry) => next.delete(buildCacheEntryKey(entry)));
      } else {
        cacheEntries.forEach((entry) => next.add(buildCacheEntryKey(entry)));
      }
      return next;
    });
  }

  async function handleRemoveSelectedCache(): Promise<void> {
    if (selectedCacheKeys.size === 0) {
      return;
    }

    if (
      !(await confirmDelete({
        title: 'Remover entradas',
        message: `Remover ${selectedCacheKeys.size} entrada(s) de cache selecionada(s)?`,
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const entries = cacheEntries
        .filter((entry) => selectedCacheKeys.has(buildCacheEntryKey(entry)))
        .map((entry) => ({
          customerId: entry.customerId,
          scope: entry.scope,
          cacheKeySuffix: entry.cacheKeySuffix,
        }));

      const removed = await removeCacheEntries(entries);
      toast.success(`${removed} entrada(s) removida(s).`);
      await refreshCacheTab(cachePage);
    } catch {
      toast.error('Falha ao remover entradas selecionadas.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClearAllCache(): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Excluir cache',
        message: 'Remover todas as entradas de cache?',
        confirmLabel: 'Excluir tudo',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const removed = await clearAllCache();
      toast.success(`${removed} entrada(s) removida(s).`);
      setCachePage(1);
      await refreshCacheTab(1);
    } catch {
      toast.error('Falha ao limpar cache.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveSingleCache(entry: CacheEntry): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Remover entrada',
        message: 'Remover esta entrada de cache?',
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await removeCacheEntries([
        {
          customerId: entry.customerId,
          scope: entry.scope,
          cacheKeySuffix: entry.cacheKeySuffix,
        },
      ]);
      toast.success('Entrada removida.');
      await refreshCacheTab(cachePage);
    } catch {
      toast.error('Falha ao remover entrada.');
    } finally {
      setActionLoading(false);
    }
  }

  function handleLogFilterSubmit(event: FormEvent): void {
    event.preventDefault();
    setAppliedLogLevel(logLevelFilter);
    setAppliedLogFromDate(logFromDate);
    setAppliedLogToDate(logToDate);
    setAppliedLogSearch(logSearch.trim());
    setLogsPage(1);
  }

  function toggleLogSelection(id: string): void {
    setSelectedLogIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAllLogsOnPage(): void {
    if (allLogsPageSelected) {
      setSelectedLogIds(new Set());
    } else {
      setSelectedLogIds(new Set(logs.map((entry) => entry.id)));
    }
  }

  async function handleRemoveSelectedLogs(): Promise<void> {
    if (selectedLogIds.size === 0) {
      return;
    }

    if (
      !(await confirmDelete({
        title: 'Remover logs',
        message: `Remover ${selectedLogIds.size} log(s) selecionado(s)?`,
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const removed = await removeMonitoringLogs(Array.from(selectedLogIds));
      toast.success(`${removed} log(s) removido(s).`);
      await loadLogs(logsPage, logsPageSize);
    } catch {
      toast.error('Falha ao remover logs selecionados.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClearAllLogs(): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Excluir logs',
        message: 'Remover todos os logs de sistema?',
        confirmLabel: 'Excluir tudo',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const removed = await clearAllMonitoringLogs();
      toast.success(`${removed} log(s) removido(s).`);
      setLogsPage(1);
      await loadLogs(1, logsPageSize);
    } catch {
      toast.error('Falha ao limpar logs.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveSingleLog(entry: MonitoringLogEntry): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Remover log',
        message: 'Remover este log?',
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await removeMonitoringLogs([entry.id]);
      toast.success('Log removido.');
      await loadLogs(logsPage, logsPageSize);
    } catch {
      toast.error('Falha ao remover log.');
    } finally {
      setActionLoading(false);
    }
  }

  function handleAuditFilterSubmit(event: FormEvent): void {
    event.preventDefault();
    setAppliedAuditAction(auditActionFilter);
    setAppliedAuditFromDate(auditFromDate);
    setAppliedAuditToDate(auditToDate);
    setAppliedAuditSearch(auditSearch.trim());
    setAuditPage(1);
  }

  function toggleAuditSelection(id: string): void {
    setSelectedAuditIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAllAuditOnPage(): void {
    if (allAuditPageSelected) {
      setSelectedAuditIds(new Set());
    } else {
      setSelectedAuditIds(new Set(auditEntries.map((entry) => entry.id)));
    }
  }

  async function handleRemoveSelectedAudit(): Promise<void> {
    if (selectedAuditIds.size === 0) {
      return;
    }

    if (
      !(await confirmDelete({
        title: 'Remover auditoria',
        message: `Remover ${selectedAuditIds.size} registro(s) de auditoria selecionado(s)?`,
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const removed = await removeMonitoringAuditEntries(Array.from(selectedAuditIds));
      toast.success(`${removed} registro(s) removido(s).`);
      await loadAudit(auditPage, auditPageSize);
    } catch {
      toast.error('Falha ao remover registros selecionados.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleClearAllAudit(): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Excluir auditoria',
        message: 'Remover todos os registros de auditoria?',
        confirmLabel: 'Excluir tudo',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const removed = await clearAllMonitoringAuditEntries();
      toast.success(`${removed} registro(s) removido(s).`);
      setAuditPage(1);
      await loadAudit(1, auditPageSize);
    } catch {
      toast.error('Falha ao limpar auditoria.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveSingleAudit(entry: MonitoringAuditEntry): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Remover registro',
        message: 'Remover este registro de auditoria?',
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await removeMonitoringAuditEntries([entry.id]);
      toast.success('Registro removido.');
      await loadAudit(auditPage, auditPageSize);
    } catch {
      toast.error('Falha ao remover registro.');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Monitoramento"
        description="Logs, auditoria e cache em memória."
      />

      <SettingsTabBar tabs={MONITORING_TABS} activeTab={activeTab} onChange={setActiveTab} />

      <div className="mt-4">
        {loading ? <LoadingSpinner label="Carregando..." /> : null}

        {!loading && activeTab === 'cache' ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-slate-600">
                Entradas em cache: <strong>{totalEntries}</strong>
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Filtrar por escopo
                  <select
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={scopeFilter}
                    onChange={(event) => {
                      setScopeFilter(event.target.value);
                      setCachePage(1);
                      setSelectedCacheKeys(new Set());
                    }}
                  >
                    <option value="">Todos</option>
                    {scopeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    title="Exclui apenas as entradas de cache marcadas na listagem"
                    className={
                      selectedCacheKeys.size > 0
                        ? 'erp-btn-danger erp-btn-sm disabled:opacity-60'
                        : 'rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60'
                    }
                    disabled={actionLoading || selectedCacheKeys.size === 0}
                    onClick={() => void handleRemoveSelectedCache()}
                  >
                    Remover selecionados ({selectedCacheKeys.size})
                  </button>
                  <button
                    type="button"
                    title="Remove todas as entradas de cache"
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                    disabled={actionLoading || totalEntries === 0}
                    onClick={() => void handleClearAllCache()}
                  >
                    Excluir tudo
                  </button>
                </div>
              </div>

              <div className="erp-table-wrap">
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <input
                            type="checkbox"
                            checked={allCachePageSelected}
                            onChange={toggleSelectAllCacheOnPage}
                            aria-label="Selecionar página"
                          />
                        </th>
                        <th>Módulo</th>
                        <th>Escopo</th>
                        <th>Chave</th>
                        <th>Gravado em</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {cacheEntries.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-slate-500">
                            Nenhuma entrada em cache para esta organização.
                          </td>
                        </tr>
                      ) : (
                        cacheEntries.map((entry) => {
                          const key = buildCacheEntryKey(entry);
                          return (
                            <tr key={key}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedCacheKeys.has(key)}
                                  onChange={() => toggleCacheEntrySelection(entry)}
                                  aria-label={`Selecionar ${entry.cacheKeySuffix}`}
                                />
                              </td>
                              <td>{entry.label}</td>
                              <td>
                                <code className="text-xs">{entry.scope}</code>
                              </td>
                              <td>
                                <code className="text-xs break-all">{entry.cacheKeySuffix}</code>
                              </td>
                              <td>{new Date(entry.setAtUtc).toLocaleString('pt-BR')}</td>
                              <td className="text-right">
                                <DeleteIconButton
                                  label="Remover esta entrada de cache"
                                  disabled={actionLoading}
                                  onClick={() => void handleRemoveSingleCache(entry)}
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <ListPaginationBar
                totalCount={entriesTotalCount}
                page={cachePage}
                pageSize={cachePageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => {
                  setCachePageSize(size);
                  setCachePage(1);
                  setSelectedCacheKeys(new Set());
                }}
                onPrevious={() => setCachePage((current) => Math.max(1, current - 1))}
                onNext={() =>
                  setCachePage((current) =>
                    current < Math.ceil(entriesTotalCount / cachePageSize) ? current + 1 : current,
                  )
                }
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-medium text-slate-700">Resumo por escopo</h3>
              <div className="erp-table-wrap">
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th>Módulo</th>
                        <th>Escopo</th>
                        <th>Entradas</th>
                        <th>Última gravação</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {cacheScopes.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-slate-500">
                            Nenhum escopo em cache.
                          </td>
                        </tr>
                      ) : (
                        cacheScopes.map((scope) => (
                          <tr key={`${scope.scope}-${scope.customerId}`}>
                            <td>{scope.label}</td>
                            <td>
                              <code className="text-xs">{scope.scope}</code>
                            </td>
                            <td>{scope.entryCount}</td>
                            <td>
                              {scope.lastSetAtUtc
                                ? new Date(scope.lastSetAtUtc).toLocaleString('pt-BR')
                                : '—'}
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                title={`Descarta todas as entradas do escopo "${scope.label}"`}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
                                disabled={invalidatingScope === scope.scope}
                                onClick={() => void handleInvalidateScope(scope)}
                              >
                                {invalidatingScope === scope.scope ? 'Invalidando...' : 'Invalidar escopo'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && activeTab === 'logs' ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <form
                className="mb-4 flex flex-wrap items-end gap-3"
                onSubmit={handleLogFilterSubmit}
              >
                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Tipo / nível
                  <select
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={logLevelFilter}
                    onChange={(event) => setLogLevelFilter(event.target.value)}
                  >
                    <option value="">Todos</option>
                    {LOG_LEVEL_OPTIONS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Data de
                  <input
                    type="date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={logFromDate}
                    onChange={(event) => setLogFromDate(event.target.value)}
                  />
                </label>

                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Data até
                  <input
                    type="date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={logToDate}
                    onChange={(event) => setLogToDate(event.target.value)}
                  />
                </label>

                <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm text-slate-600">
                  Busca
                  <input
                    type="search"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={logSearch}
                    onChange={(event) => setLogSearch(event.target.value)}
                    placeholder="Mensagem, origem..."
                  />
                </label>

                <button
                  type="submit"
                  title="Aplica os filtros de tipo, período e busca na listagem de logs"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  Filtrar
                </button>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    title="Exclui apenas os logs marcados na listagem"
                    className={
                      selectedLogIds.size > 0
                        ? 'erp-btn-danger erp-btn-sm disabled:opacity-60'
                        : 'rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60'
                    }
                    disabled={actionLoading || selectedLogIds.size === 0}
                    onClick={() => void handleRemoveSelectedLogs()}
                  >
                    Remover selecionados ({selectedLogIds.size})
                  </button>
                  <button
                    type="button"
                    title="Remove todos os logs"
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                    disabled={actionLoading || logsTotalCount === 0}
                    onClick={() => void handleClearAllLogs()}
                  >
                    Excluir tudo
                  </button>
                </div>
              </form>

              <div className="erp-table-wrap">
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <input
                            type="checkbox"
                            checked={allLogsPageSelected}
                            onChange={toggleSelectAllLogsOnPage}
                            aria-label="Selecionar página"
                          />
                        </th>
                        <th>Tipo</th>
                        <th>Data</th>
                        <th>Mensagem</th>
                        <th>Origem</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-slate-500">
                            Nenhum log de sistema para esta organização.
                          </td>
                        </tr>
                      ) : (
                        logs.map((entry) => (
                          <tr key={entry.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedLogIds.has(entry.id)}
                                onChange={() => toggleLogSelection(entry.id)}
                                aria-label={`Selecionar log ${entry.id}`}
                              />
                            </td>
                            <td>
                              <span
                                className={
                                  entry.level === 'Exception'
                                    ? 'font-medium text-rose-700'
                                    : undefined
                                }
                              >
                                {entry.level}
                              </span>
                            </td>
                            <td>{new Date(entry.timestampUtc).toLocaleString('pt-BR')}</td>
                            <td title={entry.message}>{truncateText(entry.message)}</td>
                            <td title={entry.source ?? undefined}>{truncateText(entry.source)}</td>
                            <td className="text-right">
                              <div className="inline-flex items-center gap-1">
                                <RevealIconButton
                                  label="Ver mensagem completa e stack trace"
                                  onClick={() => setDetailLog(entry)}
                                />
                                <DeleteIconButton
                                  label="Excluir este log"
                                  disabled={actionLoading}
                                  onClick={() => void handleRemoveSingleLog(entry)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <ListPaginationBar
                totalCount={logsTotalCount}
                page={logsPage}
                pageSize={logsPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => {
                  setLogsPageSize(size);
                  setLogsPage(1);
                }}
                onPrevious={() => setLogsPage((current) => Math.max(1, current - 1))}
                onNext={() =>
                  setLogsPage((current) =>
                    current < Math.ceil(logsTotalCount / logsPageSize) ? current + 1 : current,
                  )
                }
              />
            </div>
          </div>
        ) : null}

        {!loading && activeTab === 'audit' ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <form
                className="mb-4 flex flex-wrap items-end gap-3"
                onSubmit={handleAuditFilterSubmit}
              >
                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Tipo de ação
                  <select
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={auditActionFilter}
                    onChange={(event) => setAuditActionFilter(event.target.value)}
                  >
                    <option value="">Todos</option>
                    {auditActionOptions.map((action) => (
                      <option key={action} value={action}>
                        {action}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Data de
                  <input
                    type="date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={auditFromDate}
                    onChange={(event) => setAuditFromDate(event.target.value)}
                  />
                </label>

                <label className="flex flex-col gap-1 text-sm text-slate-600">
                  Data até
                  <input
                    type="date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={auditToDate}
                    onChange={(event) => setAuditToDate(event.target.value)}
                  />
                </label>

                <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm text-slate-600">
                  Busca
                  <input
                    type="search"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={auditSearch}
                    onChange={(event) => setAuditSearch(event.target.value)}
                    placeholder="Entidade, usuário, detalhes..."
                  />
                </label>

                <button
                  type="submit"
                  title="Aplica os filtros de tipo, período e busca na listagem de auditoria"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  Filtrar
                </button>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    title="Exclui apenas os registros de auditoria marcados na listagem"
                    className={
                      selectedAuditIds.size > 0
                        ? 'erp-btn-danger erp-btn-sm disabled:opacity-60'
                        : 'rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60'
                    }
                    disabled={actionLoading || selectedAuditIds.size === 0}
                    onClick={() => void handleRemoveSelectedAudit()}
                  >
                    Remover selecionados ({selectedAuditIds.size})
                  </button>
                  <button
                    type="button"
                    title="Remove todos os registros de auditoria"
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                    disabled={actionLoading || auditTotalCount === 0}
                    onClick={() => void handleClearAllAudit()}
                  >
                    Excluir tudo
                  </button>
                </div>
              </form>

              <div className="erp-table-wrap">
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <input
                            type="checkbox"
                            checked={allAuditPageSelected}
                            onChange={toggleSelectAllAuditOnPage}
                            aria-label="Selecionar página"
                          />
                        </th>
                        <th>Ação</th>
                        <th>Data</th>
                        <th>Entidade</th>
                        <th>Usuário</th>
                        <th>Detalhes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {auditEntries.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-slate-500">
                            Nenhum registro de auditoria para esta organização.
                          </td>
                        </tr>
                      ) : (
                        auditEntries.map((entry) => (
                          <tr key={entry.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedAuditIds.has(entry.id)}
                                onChange={() => toggleAuditSelection(entry.id)}
                                aria-label={`Selecionar auditoria ${entry.id}`}
                              />
                            </td>
                            <td>{entry.action}</td>
                            <td>{new Date(entry.timestampUtc).toLocaleString('pt-BR')}</td>
                            <td>
                              {entry.entityType}
                              {entry.entityId ? ` · ${truncateText(entry.entityId, 24)}` : ''}
                            </td>
                            <td>{entry.userName || '—'}</td>
                            <td title={entry.details ?? undefined}>{truncateText(entry.details)}</td>
                            <td className="text-right">
                              <DeleteIconButton
                                label="Excluir este registro de auditoria"
                                disabled={actionLoading}
                                onClick={() => void handleRemoveSingleAudit(entry)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <ListPaginationBar
                totalCount={auditTotalCount}
                page={auditPage}
                pageSize={auditPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => {
                  setAuditPageSize(size);
                  setAuditPage(1);
                }}
                onPrevious={() => setAuditPage((current) => Math.max(1, current - 1))}
                onNext={() =>
                  setAuditPage((current) =>
                    current < Math.ceil(auditTotalCount / auditPageSize) ? current + 1 : current,
                  )
                }
              />
            </div>
          </div>
        ) : null}
      </div>

      <MonitoringLogDetailModal
        entry={detailLog}
        open={detailLog !== null}
        onClose={() => setDetailLog(null)}
      />
    </PageShell>
  );
}
